import { lazy } from 'react';

// project-imports
import Loadable from '../components/Loadable';
import DashboardLayout from '../layout/Dashboard';
import { useAuth } from 'hooks';
import { Navigate } from 'react-router-dom';
import { isAdmin } from 'lib';
// render - sample page
const Login = Loadable(lazy(() => import('pages-new/auth/auth1/Login')));
const Reset = Loadable(lazy(() => import('pages-new/auth/auth1/Reset')));
const Forget = Loadable(lazy(() => import('pages-new/auth/auth1/Forget')));
const QuickHits = Loadable(lazy(() => import('pages-new/auth/auth1/QuickHits/index')));
const FileHub = Loadable(lazy(() => import('pages-new/auth/auth1/FileHub/index')));
const AdvancedAnalyticsPage = Loadable(lazy(() => import('pages-new/auth/auth1/ReportBuilder/AdvancedAnalyticsPage')));
const CurrentNotes = Loadable(lazy(() => import('pages-new/auth/auth1/Accordian/CurrentNotes/Notes')));
const SensitiveIssue = Loadable(lazy(() => import('pages-new/auth/auth1/Accordian/SensitiveIssue/Issue')));
const HealthCheck = Loadable(lazy(() => import('pages-new/auth/auth1/Accordian/HealthCheck/Health')));
const ActionRequired = Loadable(lazy(() => import('pages-new/auth/auth1/Accordian/ActionRequired/Action')));
const KeepInView = Loadable(lazy(() => import('pages-new/auth/auth1/Accordian/KeepInView/View')));
const CareManager = Loadable(lazy(() => import('pages-new/auth/auth1/Accordian/CareManagerTasks/CareManager')));
//Care Planing
const ClientIndex = Loadable(lazy(() => import('pages-new/auth/auth1/CarePlanning/Client/index')));
const ClientDetails = Loadable(lazy(() => import('pages-new/auth/auth1/CarePlanning/Client/InsideClient/index')));
const CarePartner = Loadable(lazy(() => import('pages-new/auth/auth1/CarePlanning/CarePartners/index')));
const CarePartnerDetails = Loadable(lazy(() => import('pages-new/auth/auth1/CarePlanning/CarePartners/Details')));
const CareManagement = Loadable(lazy(() => import('pages-new/auth/auth1/CarePlanning/CareManagement/index')));
//Applicants
const ApplicantsIndex = Loadable(lazy(() => import('pages-new/auth/auth1/Recruiting/Applicants/index')));
const ApplicantsDetails = Loadable(lazy(() => import('pages-new/auth/auth1/Recruiting/Applicants/InsideApplicant/InsideApplicant')));
//Marketing
const ReferralIndex = Loadable(lazy(() => import('pages-new/auth/auth1/Marketing/Referral/index')));
const EditReferralPartner = Loadable(lazy(() => import('pages-new/auth/auth1/Marketing/Referral/InsideReferral')));
const CompanyDetail = Loadable(lazy(() => import('pages-new/auth/auth1/Marketing/Referral/company')));
const ProspectIndex = Loadable(lazy(() => import('pages-new/auth/auth1/Marketing/Prospect/index')));
const ProspectDetail = Loadable(lazy(() => import('pages-new/auth/auth1/Marketing/Prospect/details/InsideProspect')));
const ActivityIndex = Loadable(lazy(() => import('pages-new/auth/auth1/Marketing/Activity/index')));
const ApplicantActivityIndex = Loadable(lazy(() => import('pages-new/auth/auth1/Recruiting/Applicants/Activity/index')));
const AwardManagementIndex = Loadable(lazy(() => import('pages-new/auth/auth1/CarePlanning/AwardManagement/index')));
const CareManagerDashboardIndex = Loadable(lazy(() => import('pages-new/auth/auth1/CarePlanning/CareManagerDashboard/index')));
const MarketingDashboardIndex = Loadable(lazy(() => import('pages-new/auth/auth1/Marketing/Dashboard/index.jsx')));
const AuthorizationIndex = Loadable(lazy(() => import('pages-new/auth/auth1/Authorization/index')));
const AuthorizationActivityIndex = Loadable(lazy(() => import('pages-new/auth/auth1/Authorization/Activity/index')));
const CareManagementReporting = Loadable(lazy(() => import('pages-new/auth/auth1/Reporting/CareManagement/index')));
const ApplicantReporting = Loadable(lazy(() => import('pages-new/auth/auth1/Reporting/Applicants/index')));
const ReferralPartnerReporting = Loadable(lazy(() => import('pages-new/auth/auth1/Reporting/ReferralPartners/index')));
const ProspectsReporting = Loadable(lazy(() => import('pages-new/auth/auth1/Reporting/Prospects/index')));
const UserManagement = Loadable(lazy(() => import('pages-new/auth/auth1/Admin/UserManagement/UserManagement')));
const StageManagement = Loadable(lazy(() => import('pages-new/auth/auth1/Admin/StageManagement/StageManagement')));
const MergeRecordsPage = Loadable(lazy(() => import('pages-new/auth/auth1/Admin/MergeRecords/index')));
const Entry = Loadable(lazy(() => import('pages-new/auth/auth1/Admin/Entry')));
const ApplicantsDashboardIndex = Loadable(lazy(() => import('pages-new/auth/auth1/Recruiting/ApplicantsDashboard/index')));
// ==============================|| MAIN ROUTES ||============================== //

const ProtectedAdminRoute = ({ children }) => {
  const { user, logout } = useAuth();
  if (user == null) {
    logout();
    return <Navigate to="/" replace />;
  }
  return isAdmin(user?.roles) ? children : <Navigate to="/quick-hits" replace />;
};
const ValidUserRoute = ({ children }) => {
  const { user, logout } = useAuth();
  if (user == null) {
    logout();
    return <Navigate to="/" replace />;
  }
  return children;
};

const MainRoutes = {
  path: '/',
  children: [
    {
      path: '/',
      children: [
        {
          path: '/',
          element: <Login />
        },
        {
          path: 'password-reset/:resetToken',
          element: <Reset />
        },
        {
          path: 'forget',
          element: <Forget />
        }
      ]
    },
    {
      path: '/',
      element: <ValidUserRoute><DashboardLayout /></ValidUserRoute>,
      children: [
        {
          path: 'prospects',
          element: <ProspectIndex />
        },
        {
          path: 'prospects/:id',
          element: <ProspectDetail />
        },
        {
          path: 'referral',
          element: <ReferralIndex />
        },
        {
          path: 'activity',
          element: <ActivityIndex />
        }
      ]
    },
    {
      path: '/',
      element: <ValidUserRoute><DashboardLayout /></ValidUserRoute>,
      children: [
        {
          path: 'client',
          element: <ClientIndex />
        },
        {
          path: 'client/:id',
          element: <ClientDetails />
        },
        {
          path: 'care-partner',
          element: <CarePartner />
        },
        {
          path: 'care-partner/:id',
          element: <CarePartnerDetails />
        },
        {
          path: 'care-management',
          element: <CareManagement />
        },
        {
          path: 'award-management',
          element: <AwardManagementIndex />
        },
        {
          path: 'care-manager-dashboard',
          element: <CareManagerDashboardIndex />
        },
        {
          path: 'marketing/dashboard',
          element: <MarketingDashboardIndex />
        },
        {
          path: 'authorization',
          element: <AuthorizationIndex />
        },
        {
          path: 'authorization/activity',
          element: <AuthorizationActivityIndex />
        }
      ]
    },
    {
      path: '/',
      element: <ValidUserRoute><DashboardLayout /></ValidUserRoute>,
      children: [
        {
          path: 'quick-hits',
          element: <QuickHits />,
          children: [
            {
              path: 'current-notes',
              element: <CurrentNotes />
            },
            {
              path: 'sensitive-issue',
              element: <SensitiveIssue />
            },
            {
              path: 'action-required',
              element: <ActionRequired />
            },
            {
              path: 'health-check',
              element: <HealthCheck />
            },
            {
              path: 'keep-view',
              element: <KeepInView />
            },
            {
              path: 'care-manager',
              element: <CareManager />
            }
          ]
        }
      ]
    },
    {
      path: '/',
      element: <ValidUserRoute><DashboardLayout /></ValidUserRoute>,
      children: [
        {
          path: 'applicants',
          element: <ApplicantsIndex />
        },
        {
          path: 'applicants/:id',
          element: <ApplicantsDetails />
        },
        {
          path: 'applicants/activity',
          element: <ApplicantActivityIndex />
        },
          {
          path: 'applicants/dashboard',
          element: <ApplicantsDashboardIndex />
        },
        // {
        //   path: 'snapshot',
        //   element: <SnapshotsIndex />
        // },
        // {
        //   path: 'status',
        //   element: <StatusIndex />
        // },

      ]
    },
    {
      path: '/',
      element: <ValidUserRoute><DashboardLayout /></ValidUserRoute>,
      children: [
        {
          path: '/reporting/care-management',
          element: <CareManagementReporting />
        },
        {
          path: '/reporting/applicants',
          element: <ApplicantReporting />
        },
        {
          path: '/reporting/prospects',
          element: <ProspectsReporting />
        },
        {
          path: '/reporting/referral-partner',
          element: <ReferralPartnerReporting />
        },
        {
          path: 'referral-partner/:id',
          element: <EditReferralPartner />
        },
        {
          path: 'marketing/company/:id',
          element: <CompanyDetail />
        }
      ]
    },
    {
      path: '/',
      element: <DashboardLayout />,
      children: [
        {
          path: 'file-hub',
          element: <FileHub />
        }
      ]
    },
    {
      path: '/',
      element: <DashboardLayout />,
      children: [
        {
          path: 'report-builder',
          element: <AdvancedAnalyticsPage />
        },
        {
          path: 'advanced-report-builder',
          element: <AdvancedAnalyticsPage />
        }
      ]
    },
    {
      path: '/',
      element: <ProtectedAdminRoute><DashboardLayout /></ProtectedAdminRoute>,
      children: [
        { path: 'user-management', element: <UserManagement /> },
        { path: 'stage-management', element: <StageManagement /> },
        { path: 'merge-records', element: <MergeRecordsPage /> },
        { path: 'vendor-management', element: <Entry key="vendor" name="Vendor Management" buttonTitle="Vendor" url="vendors" title="Award Vendors" objkey="vendor" /> },
        { path: 'award-type-management', element: <Entry key="awardName" name="Award Type Management" buttonTitle="Award Type" url="awards" title="Award Type" objkey="awardName" /> },
        { path: 'activity-management', element: <Entry key="activity" name="Activity Management" buttonTitle="Activity" url="activity" title="Activity" objkey="activity" /> },
        { path: 'location-management', element: <Entry key="location" name="Location Management" buttonTitle="Location" url="location" title="Location Management" objkey="location" /> },
        { path: 'prospect-activity', element: <Entry key="prospect-type" name="Prospect Activity" buttonTitle="Prospect Activity" url="marketing/type" title="Prospect Activity" objkey="type" /> },
        { path: 'referral-partners-type', element: <Entry key="referral-partners-type" name="Referral Partners Type" buttonTitle="Referral Partners Type" url="referral-partners" title="Referral Partners" objkey="companyType" /> },
        { path: 'referral-partners-activity', element: <Entry key="referral-partners-activity" name="Referral Partners Activity" buttonTitle="Referral Partners Activity" url="marketing/type" title="Referral Partners Activity" objkey="type" /> },
        { path: 'lead-source', element: <Entry key="lead-source" name="Lead Source" buttonTitle="Lead Source" url="lead-source" title="Lead Source" objkey="type" /> },
        { path: 'applicant-source', element: <Entry key="applicant-source" name="Applicant Source" buttonTitle="Applicant Source" url="applicant-source" title="Applicant Source" objkey="type" /> },
        { path: 'applicant-threshold', element: <Entry key="applicant-threshold" name="Applicant Threshold" buttonTitle="Applicant Threshold" url="applicant-threshold" title="Applicant Threshold" objkey="type" /> },
        { path: 'stage-options', element: <Entry key="stage-options" name="Stage Options" buttonTitle="Stage Options" url="stage-options" title="Prospect Stage" objkey="type" /> },
        { path: 'applicant-stage', element: <Entry key="applicant-stage" name="Applicant Stage Options" buttonTitle="Applicant Stage Options" url="applicant-stage" title="Applicant Stage Options" objkey="type" /> },
        { path: 'lost-client-options', element: <Entry key="lost-client-options" name="Lead Deactivation Reasons" buttonTitle="Lost Client Options" url="lost-client" title="Lost Client Reasons" objkey="type" /> },
        { path: 'contact-types', element: <Entry key="contact-types" name="Contact Types" buttonTitle="Contact Types" url="contact-type" title="Contact Types" objkey="type" /> },
        { path: 'contact-relationships', element: <Entry key="contact-relationships" name="Contact Relationships" buttonTitle="Contact Relationships" url="contact-relationships" title="Contact Types" objkey="type" /> },
        { path: 'client-deactivation', element: <Entry key="client-deactivation" name="Client Deactivation Reasons" buttonTitle="Client Deactivation Reasons" url="client" title="Contact Deactivation Reasons" objkey="type" /> },
        { path: 'care-partner-deactivation', element: <Entry key="care-partner-deactivation" name="Care Partner Deactivation Reasons" buttonTitle="Care Partner Deactivation Reasons" url="care-partner" title="Care Partner Deactivation Reasons" objkey="type" /> },
        { path: 'file-hub-sections', element: <Entry key="file-hub" name="File Uploader Sections" buttonTitle="File Uploader Sections" url="file-hub" title="File Upload Sections" objkey="type" /> },
        { path: 'rating-referral', element: <Entry key="rating-referral" name="Rating Referral" buttonTitle="Rating Referral" url="rating-referral" title="Rating Referral" objkey="type" /> },
        { path: 'rating-referral-company', element: <Entry key="rating-referral-company" name="Rating Referral Company" buttonTitle="Rating Referral Company" url="rating-referral-company" title="Rating Referral Company" objkey="type" /> },
        { path: 'priority-index', element: <Entry key="priority-index" name="Priority Rating" buttonTitle="Priority Rating" url="priority-index" title="Priority Rating" objkey="type" /> },
        { path: 'priority-index-cp', element: <Entry key="priority-index-cp" name="Priority Rating Care Partner" buttonTitle="Priority Rating Care Partner" url="priority-index-cp" title="Priority Rating Care Partner" objkey="type" /> },

        { path: 'applicant-activity-type', element: <Entry key="applicant-activity-type" name="Applicant Activity Type" buttonTitle="Applicant Activity Type" url="applicant-activity-type" title="Applicant Activity Type" objkey="type" /> },
        { path: 'applicant-activity-type-category', element: <Entry key="applicant-activity-type-category" name="Applicant Activity Type Category" buttonTitle="Applicant Activity Type Category" url="applicant-activity-type-category" title="Applicant Activity Type Category" objkey="type" /> },
        { path: 'activity-category-management', element: <Entry key="activity-category-management" name="Activity Category Management" buttonTitle="Activity Category Management" url="activity-category-management" title="Activity Category Management" objkey="type" /> },
        { path: 'marketing-activity-type-category', element: <Entry key="marketing-activity-type-category" name="Marketing Activity Type Category" buttonTitle="Marketing Activity Type Category" url="marketing-activity-type-category" title="Marketing Activity Type Category" objkey="type" /> },
        { path: 'referral-partners-activity-category', element: <Entry key="referral-partners-activity-category" name="Referral Partners Activity Category" buttonTitle="Referral Partners Activity Category" url="marketing-activity-type-category" title="Referral Partners Activity Category" objkey="type" /> },
        { path: 'prospect-activity-category', element: <Entry key="prospect-activity-category" name="Prospect Activity Category" buttonTitle="Prospect Activity Category" url="marketing-activity-type-category" title="Prospect Activity Category" objkey="type" /> },
        { path: 'authorization-types', element: <Entry key="authorization-types" name="Authorization Types" buttonTitle="Authorization Types" url="authorization-types" title="Authorization Types" objkey="type" /> },
        { path: 'tags', element: <Entry key="tags" name="Tags" buttonTitle="Tags" url="tags" title="Tags" objkey="tagName" /> },
        { path: 'tag-category', element: <Entry key="tag-category" name="Tag Category" buttonTitle="Tag Category" url="tag-category" title="Tag Category" objkey="type" /> },

        { path: 'disciplinary-step', element: <Entry key="disciplinary-step" name="Disciplinary Step" buttonTitle="Disciplinary Step" url="disciplinary-step" title="Disciplinary Step" objkey="type" /> },
        { path: 'disciplinary-category', element: <Entry key="disciplinary-category" name="Disciplinary Category" buttonTitle="Disciplinary Category" url="disciplinary-category" title="Disciplinary Category" objkey="type" /> },
        { path: 'disciplinary-ack', element: <Entry key="disciplinary-ack" name="Disciplinary CP Acknowledgment" buttonTitle="Disciplinary CP Acknowledgment" url="disciplinary-ack" title="Disciplinary CP Acknowledgment" objkey="type" /> },
        { path: 'disciplinary-attendance-type', element: <Entry key="disciplinary-attendance-type" name="Disciplinary Attendance Type" buttonTitle="Disciplinary Attendance Type" url="disciplinary-attendance-type" title="Disciplinary Attendance Type" objkey="type" /> },
      ]
    }
  ]
};

export default MainRoutes;
